import { useEffect, useRef } from 'react';
import './VicegripAnimation.css';

export default function VicegripAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container
    const container = playerEl.closest('.battle-container');
    if (!container) {
      console.error('Battle container not found');
      onComplete();
      return;
    }

    // Get positions for opponent
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of opponent sprite
    const frameWidth = 192;  // Width of each frame (960px total ÷ 5 frames)
    const frameHeight = 192; // Height of the sprite
    const centerX = o.left - containerRect.left + o.width / 2;
    const centerY = o.top - containerRect.top + o.height / 2;

    // Initial player animation (slight scale before attack)
    playerEl.style.transition = 'transform 0.2s ease-in-out';
    playerEl.style.transform = 'scale(1.1)';
    
    // Create and position animation element
    const anim = document.createElement('div');
    anim.className = 'vice-grip-anim';
    anim.style.setProperty('--sx', `${centerX - frameWidth / 2}px`);
    anim.style.setProperty('--sy', `${centerY - frameHeight / 2}px`);
    container.appendChild(anim);
    animRef.current = anim;
    
    // Create grip effects at impact moment (frame 3)
    const gripEffectTimer = setTimeout(() => {
      // Flash effect with crushing color
      const flash = document.createElement('div');
      flash.className = 'vice-grip-flash';
      flash.style.left = `${centerX}px`;
      flash.style.top = `${centerY}px`;
      container.appendChild(flash);
      
      // Create pressure lines showing the grip force
      for (let i = 0; i < 6; i++) {
        const pressureLine = document.createElement('div');
        pressureLine.className = 'vice-grip-pressure-line';
        pressureLine.style.left = `${centerX}px`;
        pressureLine.style.top = `${centerY}px`;
        pressureLine.style.setProperty('--angle', `${(i * 60)}deg`);
        pressureLine.style.setProperty('--delay', `${i * 0.05}s`);
        container.appendChild(pressureLine);
        
        // Remove pressure line after animation
        setTimeout(() => {
          if (pressureLine.parentNode) {
            pressureLine.parentNode.removeChild(pressureLine);
          }
        }, 600);
      }
      
      // Create crushing particles
      for (let i = 0; i < 8; i++) {
        const particle = document.createElement('div');
        particle.className = 'vice-grip-particle';
        particle.style.left = `${centerX}px`;
        particle.style.top = `${centerY}px`;
        particle.style.setProperty('--angle', `${(i * 45)}deg`);
        particle.style.setProperty('--distance', `${30 + Math.random() * 25}px`);
        particle.style.setProperty('--delay', `${Math.random() * 0.1}s`);
        container.appendChild(particle);
        
        // Remove particle after animation
        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
          }
        }, 700);
      }
      
      // Create grip squeeze effect
      const squeeze = document.createElement('div');
      squeeze.className = 'vice-grip-squeeze';
      squeeze.style.left = `${centerX}px`;
      squeeze.style.top = `${centerY}px`;
      container.appendChild(squeeze);
      
      // Remove flash after animation
      setTimeout(() => {
        if (flash.parentNode) {
          flash.parentNode.removeChild(flash);
        }
      }, 400);
      
      // Remove squeeze after animation
      setTimeout(() => {
        if (squeeze.parentNode) {
          squeeze.parentNode.removeChild(squeeze);
        }
      }, 800);
      
      // Opponent hit effect - crushing/squeezing effect
      opponentEl.style.transition = 'transform 0.2s ease-in-out, filter 0.15s ease-in-out';
      opponentEl.style.transform = 'scaleX(0.9) scaleY(1.1)';
      opponentEl.style.filter = 'brightness(0.8) contrast(1.3) saturate(0.7)';
      
      setTimeout(() => {
        opponentEl.style.transform = '';
        opponentEl.style.filter = '';
      }, 200);
      
    }, 420); // Frame 3 timing (0.7s * 3/5 = 0.42s)
    
    // Reset player scale
    setTimeout(() => {
      playerEl.style.transform = '';
    }, 200);
    
    // Complete animation
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 1000);
    
    // Cleanup function
    return () => {
      clearTimeout(gripEffectTimer);
      clearTimeout(completeTimer);
      
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
      }
      
      // Reset player styles
      if (playerEl) {
        playerEl.style.transition = '';
        playerEl.style.transform = '';
      }
      
      // Reset opponent styles
      if (opponentEl) {
        opponentEl.style.transition = '';
        opponentEl.style.transform = '';
        opponentEl.style.filter = '';
      }
      
      // Clean up any remaining effects
      const effects = container?.querySelectorAll('.vice-grip-flash, .vice-grip-pressure-line, .vice-grip-particle, .vice-grip-squeeze');
      effects?.forEach(effect => {
        if (effect.parentNode) {
          effect.parentNode.removeChild(effect);
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set the move type for proper coloring in the battle UI
VicegripAnimation.moveType = "normal";
